import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { reportApi } from '../../../shared/components/Service';
import { buildEmbedPayload, getEmployeeInfo, getPowerBIEmployeeInfo } from '../../power-bi-portal/helpers/reportHelpers';
import { loadPowerBISecuritySyncPagesRequest } from '../redux/Actions';
import DefaultButton from '../../../shared/components/DefaultButton';

const SyncPagesModal = ({ show, handleClose, reportData }) => {
  const dispatch = useDispatch();
  const embedContainerRef = useRef(null);
  const reportRef = useRef(null);

  // Get MDS pages from Redux state (this is MDS data from API)
  const { reportPages: mdsPages, reportPagesLoading } = useSelector((state) => state.powerBISecurity);

  const [embedConfig, setEmbedConfig] = useState(null);
  const [powerBIPages, setPowerBIPages] = useState([]);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setPowerBIPages([]);
      setEmbedConfig(null);
      setIsLoadingPages(false);
    }
  }, [show]);

  // Fetch embed token when modal opens
  useEffect(() => {
    const fetchEmbedToken = async () => {
      if (show && reportData) {
        console.log('=== SYNC PAGES MODAL DEBUG ===');
        console.log('1. Modal opened with reportData:', reportData);
        setIsLoadingPages(true);

        try {
          const empInfo = getEmployeeInfo();
          const biEmpInfo = getPowerBIEmployeeInfo();

          const extractedRoles = reportData.group_role?.map(gr => {
            return gr.role || gr.role_code;
          }).filter(Boolean) || [];

          const reportWithRoles = {
            reportId: reportData.report_id,
            datasetId: "596b8463-5606-48fe-8951-41e720c05f73",
            role_code: extractedRoles,
            identity: reportData.identity
          };

          console.log('2. Report with roles:', reportWithRoles);

          const embedPayload = buildEmbedPayload(reportWithRoles, empInfo, biEmpInfo);
          console.log('3. Embed payload built');

          const response = await reportApi.post('/powerbi/embed-token', embedPayload);
          console.log('4. Embed token response:', response.data);

          if (response.status === 200 && response.data) {
            const embedData = response.data.data || response.data;

            const config = {
              embedUrl: reportData.embedUrl || embedData.embedUrl,
              accessToken: embedData.accessToken || embedData.embedToken,
              reportId: reportData.report_id,
            };

            console.log('5. Setting embed config:', config);
            console.log('   - embedUrl from:', reportData.embedUrl ? 'reportData' : 'embedData');
            console.log('   - accessToken exists:', !!(embedData.accessToken || embedData.embedToken));
            setEmbedConfig(config);
          }
        } catch (error) {
          console.error('ERROR fetching embed token:', error);
          setIsLoadingPages(false);
        }
      } else {
        console.log('=== SYNC PAGES MODAL - MISSING DATA ===');
        console.log('show:', show);
        console.log('reportData:', reportData);
        console.log('embedUrl:', reportData?.embedUrl);
      }
    };

    fetchEmbedToken();
  }, [show, reportData]);


  // Handle sync pages using Redux
  const handleSyncPages = () => {
    setIsSyncing(true);

    const pages = powerBIPages.map(page => ({
      page_id: page.name,
      page_name: page.displayName,
      visibility: page.visibility === 0 ? true : false,
    }));

    // Dispatch Redux action with registryId from reportData
    dispatch(loadPowerBISecuritySyncPagesRequest(
      reportData.report_id,
      reportData.id, // registryId
      pages
    ));

    // Close modal after dispatching
    setTimeout(() => {
      setIsSyncing(false);
      handleClose();
    }, 500);
  };

  const reportIcon = reportData?.report_icon || reportData?.icon || 'bar-chart';
  const colorCode = reportData?.color_code || '#1a2332';

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '50px',
            height: '50px',
            backgroundColor: colorCode,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <i className={`fa fa-${reportIcon}`} style={{ fontSize: '1.5rem', color: '#ffffff' }}></i>
          </div>
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600' }}>
              Sync Pages — {reportData?.report_name}
            </div>
            <div className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>
              Report ID: {reportData?.report_id}
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '1.5rem' }}>
        {(reportPagesLoading || isLoadingPages) && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading pages...</span>
            </div>
            <p className="mt-2">
              {reportPagesLoading ? 'Fetching MDS pages...' : 'Loading Power BI report...'}
            </p>
          </div>
        )}

        {!reportPagesLoading && !isLoadingPages && (
          <>
            {/* Two Side-by-Side Tables */}
            <div className="row">
              {/* MDS Pages - Left Side */}
              <div className="col-md-6">
                <div className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '8px' }}>
                  <i className="fa fa-database" style={{ fontSize: '1.5rem', color: '#1967d2' }}></i>
                  <div>
                    <h5 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>MDS Pages ({mdsPages?.length || 0})</h5>
                    <small className="text-muted">Pages currently available in MDS.</small>
                  </div>
                </div>
                <div className="border rounded" style={{ backgroundColor: '#ffffff', maxHeight: '500px', overflowY: 'auto' }}>
                  {(!mdsPages || mdsPages.length === 0) ? (
                    <div className="text-center text-muted py-5">
                      <i className="fa fa-info-circle fa-2x mb-2"></i>
                      <p>No MDS pages available</p>
                    </div>
                  ) : (
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                      <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                          <th style={{ width: '50px', padding: '12px' }}>#</th>
                          <th style={{ padding: '12px' }}>Page Name</th>
                          <th style={{ padding: '12px' }}>Page ID</th>
                          <th style={{ width: '120px', textAlign: 'center', padding: '12px' }}>Visibility</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mdsPages.map((page, index) => (
                          <tr key={index}>
                            <td style={{ padding: '12px' }}>{index + 1}</td>
                            <td style={{ padding: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="fa fa-file-text-o" style={{ color: '#5f6368' }}></i>
                                <strong>{page.page_name || page.displayName || page.name}</strong>
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <code style={{ fontSize: '0.75rem', color: '#5f6368', backgroundColor: '#f8f9fa', padding: '4px 8px', borderRadius: '4px' }}>
                                {page.page_id || page.name}
                              </code>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {page.visibility ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#137333', fontSize: '0.85rem', backgroundColor: '#e6f4ea', padding: '4px 10px', borderRadius: '12px' }}>
                                  <i className="fa fa-eye"></i>
                                  Visible
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#5f6368', fontSize: '0.85rem', backgroundColor: '#f1f3f4', padding: '4px 10px', borderRadius: '12px' }}>
                                  <i className="fa fa-eye-slash"></i>
                                  Hidden
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                  Total: {mdsPages?.length || 0} pages
                </div>
              </div>

              {/* Power BI Pages - Right Side */}
              <div className="col-md-6">
                <div className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#fff4e5', borderRadius: '8px' }}>
                  <i className="fa fa-bar-chart" style={{ fontSize: '1.5rem', color: '#ea8600' }}></i>
                  <div>
                    <h5 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600' }}>Power BI Pages ({powerBIPages?.length || 0})</h5>
                    <small className="text-muted">Pages available in Power BI.</small>
                  </div>
                </div>
                <div className="border rounded" style={{ backgroundColor: '#ffffff', maxHeight: '500px', overflowY: 'auto' }}>
                  {(!powerBIPages || powerBIPages.length === 0) ? (
                    <div className="text-center text-muted py-5">
                      <i className="fa fa-info-circle fa-2x mb-2"></i>
                      <p>No Power BI pages available</p>
                    </div>
                  ) : (
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                      <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                          <th style={{ width: '50px', padding: '12px' }}>#</th>
                          <th style={{ padding: '12px' }}>Page Name</th>
                          <th style={{ padding: '12px' }}>Page ID</th>
                          <th style={{ width: '120px', textAlign: 'center', padding: '12px' }}>Visibility</th>
                        </tr>
                      </thead>
                      <tbody>
                        {powerBIPages.map((page, index) => (
                          <tr key={index}>
                            <td style={{ padding: '12px' }}>{index + 1}</td>
                            <td style={{ padding: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="fa fa-file-text-o" style={{ color: '#5f6368' }}></i>
                                <strong>{page.displayName}</strong>
                              </div>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <code style={{ fontSize: '0.75rem', color: '#5f6368', backgroundColor: '#f8f9fa', padding: '4px 8px', borderRadius: '4px' }}>
                                {page.name}
                              </code>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {page.visibility === 0 ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#137333', fontSize: '0.85rem', backgroundColor: '#e6f4ea', padding: '4px 10px', borderRadius: '12px' }}>
                                  <i className="fa fa-eye"></i>
                                  Visible
                                </span>
                              ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#5f6368', fontSize: '0.85rem', backgroundColor: '#f1f3f4', padding: '4px 10px', borderRadius: '12px' }}>
                                  <i className="fa fa-eye-slash"></i>
                                  Hidden
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                  Total: {powerBIPages?.length || 0} pages
                </div>
              </div>
            </div>
          </>
        )}

        {/* Hidden PowerBI Embed - Only to fetch pages */}
        {(() => {
          if (embedConfig && embedConfig.embedUrl && embedConfig.accessToken) {
            console.log('5.75. ✅ PowerBIEmbed component is rendering with config:', embedConfig);
            return true;
          } else {
            console.log('5.75. ❌ PowerBIEmbed NOT rendering. embedConfig:', embedConfig);
            return false;
          }
        })()}
        {embedConfig && embedConfig.embedUrl && embedConfig.accessToken && (
          <div ref={embedContainerRef} style={{ position: 'absolute', left: '-9999px', width: '1000px', height: '1000px'}}>
            <PowerBIEmbed
              embedConfig={{
                type: 'report',
                id: embedConfig.reportId,
                embedUrl: embedConfig.embedUrl,
                accessToken: embedConfig.accessToken,
                tokenType: models.TokenType.Embed,
                settings: {
                  panes: {
                    filters: { visible: false },
                    pageNavigation: { visible: false }
                  },
                },
              }}
              eventHandlers={
                new Map([
                  ['loaded', async () => {
                    console.log('6. ✅ Power BI Report LOADED event fired');

                    // Try to fetch pages immediately after load (don't wait for rendered)
                    try {
                      if (reportRef.current && typeof reportRef.current.getPages === 'function') {
                        console.log('6.5. Attempting to fetch pages from LOADED event...');
                        const pages = await reportRef.current.getPages();
                        console.log('6.6. getPages() returned from LOADED event:', pages);

                        const pagesData = pages.map(p => ({
                          name: p.name,
                          displayName: p.displayName,
                          isActive: p.isActive,
                          visibility: p.visibility,
                        }));

                        console.log('6.7. ✅ Mapped pages data from LOADED:', pagesData);
                        setPowerBIPages(pagesData);
                        setIsLoadingPages(false);
                        console.log('6.8. ✅ setPowerBIPages called from LOADED event');
                      }
                    } catch (error) {
                      console.error('❌ Error fetching pages from LOADED event:', error);
                      console.log('Will wait for RENDERED event...');
                    }
                  }],
                  ['rendered', async () => {
                    console.log('7. ✅ Power BI Report RENDERED - now fetching pages...');
                    try {
                      if (reportRef.current && typeof reportRef.current.getPages === 'function') {
                        console.log('8. reportRef.current exists, calling getPages()...');
                        const pages = await reportRef.current.getPages();
                        console.log('9. getPages() returned:', pages);

                        const pagesData = pages.map(p => ({
                          name: p.name,
                          displayName: p.displayName,
                          isActive: p.isActive,
                          visibility: p.visibility,
                        }));

                        console.log('10. ✅ Mapped pages data:', pagesData);
                        setPowerBIPages(pagesData);
                        setIsLoadingPages(false);
                        console.log('11. ✅ setPowerBIPages called, isLoadingPages set to false');
                      } else {
                        console.error('❌ reportRef.current.getPages is not available');
                        console.log('reportRef.current:', reportRef.current);
                        setIsLoadingPages(false);
                      }
                    } catch (error) {
                      console.error('❌ Error fetching pages:', error);
                      setIsLoadingPages(false);
                    }
                  }],
                  ['error', (event) => {
                    console.error('❌ Report embedding error:', event.detail);
                    setIsLoadingPages(false);
                  }],
                ])
              }
              getEmbeddedComponent={(embeddedReport) => {
                console.log('5.5. ✅ getEmbeddedComponent called, setting reportRef');
                reportRef.current = embeddedReport;
                window.report = embeddedReport;
                console.log('reportRef set to:', embeddedReport);
              }}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ padding: '1rem 1.5rem', backgroundColor: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <i className="fa fa-info-circle" style={{ fontSize: '1.2rem', color: '#1967d2' }}></i>
          <span style={{ color: '#5f6368', fontSize: '0.9rem' }}>
            This will sync the Power BI pages with MDS for this report.
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <DefaultButton
            buttonClass="btn btn-secondary"
            buttonTitle="Cancel"
            actionBlock={handleClose}
          />
          <DefaultButton
            buttonClass="default-btn-color"
            buttonTitle={isSyncing ? 'Syncing...' : 'Sync Power BI Pages'}
            actionBlock={handleSyncPages}
            disabled={isSyncing || powerBIPages.length === 0}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default SyncPagesModal;
